import * as React from "react";
import styled from "styled-components";

interface IPaginationButtonsProps {
  pages: number;
  page: number;
  buttons: IButtonsProps;
}

interface IButtonsProps {
  previous: IButtonProps;
  next: IButtonProps;
  items: IButtonProps[];
}

interface IButtonProps {
  active?: boolean;
  disabled?: boolean;
  title: string;
  onClick: () => void;
}

const PaginationButtonsStyled = styled.div`
  position: relative;
  padding-left: 72px;
  padding-right: 72px;
  max-width: 454px;
  overflow: hidden;

  button {
    transition: ${({ theme }) => theme.transition.quickest};
    position: relative;
    z-index: 1;
    margin: 0 0 0 -1px;
    padding: 0;
    width: 32px;
    height: 32px;
    font-family: ${({ theme }) => theme.fonts.family.body || "Roboto"}, Helvetica;
    font-size: ${({ theme }) => theme.fonts.sizes.p};
    color: ${({ theme }) => theme.fonts.colors.primary};
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.lightGrey};
    border-radius: none;
    outline: none;
    appearance: none;
    cursor: pointer;

    &:hover,
    &:active {
      z-index: 2;
      color: ${({ theme }) => theme.colors.primaryDark};
      border-style: solid;
    }

    &.active {
      color: ${({ theme }) => theme.fonts.colors.white};
      background-color: ${({ theme }) => theme.colors.primaryDark};

      &:hover {
        color: ${({ theme }) => theme.fonts.colors.white};
        background-color: ${({ theme }) => theme.colors.primaryDark};
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;

      &:hover {
        color: ${({ theme }) => theme.fonts.colors.primary};
        background-color: ${({ theme }) => theme.colors.white};
      }
    }
  }

  .next-prev {
    button {
      width: 72px;
    }

    .next,
    .previous {
      button {
        position: absolute;
        top: 0;
        margin-left: 0;
      }
    }

    .next {
      button {
        right: 1px;
        border-radius: 0 ${({ theme }) => theme.borderRadius} ${({ theme }) => theme.borderRadius} 0;
      }
    }

    .previous {
      button {
        left: 0;
        border-radius: ${({ theme }) => theme.borderRadius} 0 0 ${({ theme }) => theme.borderRadius};
      }
    }
  }

  .pages {
    li {
      float: left;
    }
  }
`;

/**
 * Button
 */
const PaginationButton: React.ReactFCWithChildren<IButtonProps> = ({
  active, disabled, title, onClick
}) => {
  const isActive = typeof active === "boolean" ? active : false;
  const classes = `${isActive ? "active" : ""}`;

  return (
    <button
      className={classes}
      disabled={disabled}
      onClick={onClick}
    >
      {title}
    </button>
  );
};

/**
 * Pagination buttons
 */
const PaginationButtons: React.ReactFCWithChildren<IPaginationButtonsProps> = ({
  pages, page, buttons
}) => {
  const numbers = buttons.items.filter((_item, index) => {
    if (pages <= 10) {
      return true;
    }

    if (index === 0 || index === 1) {
      return true;
    }

    if (page >= pages - 2 && index >= pages - 2) {
      return true;
    }

    if (index <= page + 2 && index >= page - 2) {
      return true;
    }

    if (index === buttons.items.length - 1 || index === buttons.items.length - 2) {
      return true;
    }

    return false;
  });

  return (
    <PaginationButtonsStyled>
      <ul className="next-prev">
        <li className="previous">
          <PaginationButton
            {...buttons.previous}
            disabled={page === 1}
          />
        </li>

        <li className="next">
          <PaginationButton
            {...buttons.next}
            disabled={page === pages}
          />
        </li>
      </ul>

      <ul className="pages">
        {numbers.map((item, i) => {
          if (pages > 10) {
            if (page !== 1 && page !== 2) {
              if (i === 0) {
                return (
                  <li key={i}>
                    <PaginationButton
                      active={false}
                      {...item}
                    />
                  </li>
                );
              }

              if (i === 1) {
                return (
                  <li key={i}>
                    <PaginationButton
                      disabled
                      active={false}
                      title="..."
                      onClick={() => null}
                    />
                  </li>
                );
              }
            }

            if (page !== pages && page !== pages - 1 && page !== pages - 2) {
              if (i === numbers.length - 1) {
                return (
                  <li key={i}>
                    <PaginationButton
                      active={false}
                      {...item}
                    />
                  </li>
                );
              }

              if (i === numbers.length - 2) {
                return (
                  <li key={i}>
                    <PaginationButton
                      disabled
                      active={false}
                      title="..."
                      onClick={() => null}
                    />
                  </li>
                );
              }
            }
          }

          return (
            <li key={i}>
              <PaginationButton
                {...item}
                active={page === parseInt(item.title, 10)}
              />
            </li>
          );
        })}
      </ul>
    </PaginationButtonsStyled>
  );
};

export default PaginationButtons;
